<template>
  <PbsActionButton
    v-if="props.detail"
    :label="$t('general.moreActions')"
    :items="actionButtonItems"
    :mod-vertical="modVertical"
    :mod-large="modLarge"
  />
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PbsActionButton, { PbsActionButtonItem } from '@/components/PbsActionButton.vue';
import DefinitionStatusForm from '../definition-status-form/DefinitionStatusForm.vue';
import DefinitionDuplicationForm from '../definition-duplication-form/DefinitionDuplicationForm.vue';
import DefinitionExportForm from '../definition-export-form/DefinitionExportForm.vue';
import DefinitionDryRunForm from '../definition-dryrun-form/DefinitionDryRunForm.vue';
import DefinitionCascadeDeleteForm from '../definition-cascade-delete-form/DefinitionCascadeDeleteForm.vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { DeliveryProcessDefinitionDetailDto, DeliveryProcessDefinitionSearchResultDto } from '@/api/backoffice/clients';
import { DeliveryProcessDefinitionActionButtonType } from '@/modules/configuration/common/delivery-process-definition.common';
import { useRouter } from 'vue-router';
import { useDeliveryProcessDefinitionDetailStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-detail.store';

const store = useDeliveryProcessDefinitionDetailStore();
const modalStore = useModalStore();
const router = useRouter();

const { t } = useI18n();

type DeliveryProcessDefinitionActionButtonProps = {
  detail: DeliveryProcessDefinitionDetailDto | DeliveryProcessDefinitionSearchResultDto;
  modVertical?: boolean;
  modLarge?: boolean;
  actions: DeliveryProcessDefinitionActionButtonType[];
};

const props = withDefaults(defineProps<DeliveryProcessDefinitionActionButtonProps>(), {
  modVertical: false,
  modLarge: false,
  detail: null,
  actions: () => []
});

const actionButtonItems = computed((): PbsActionButtonItem[] => {
  const items = createActionButtonItems();
  return props.actions?.map((action) => items.find((x) => x.name === action)).filter((x) => !!x) ?? [];
});

const createActionButtonItems = () => {
  const isDraft = props.detail?.statusId === 0;

  return [
    {
      name: DeliveryProcessDefinitionActionButtonType.ChangeStatus,
      label: t('deliveryprocessdefinition.actions.changeStatus.label'),
      click: showChangeStatusModal,
      ellipsis: true
    },
    {
      name: DeliveryProcessDefinitionActionButtonType.Duplicate,
      label: t('deliveryprocessdefinition.actions.duplicate.label'),
      click: showDuplicationModal,
      ellipsis: true
    },
    {
      name: DeliveryProcessDefinitionActionButtonType.DryRun,
      label: t('deliveryprocessdefinition.actions.dryrun.label'),
      click: showDryRunModal,
      ellipsis: true,
      disabled: !isDraft
    },
    {
      name: DeliveryProcessDefinitionActionButtonType.CascadeDelete,
      label: t('deliveryprocessdefinition.actions.cascadeDelete.label'),
      click: showCascadeDeleteModal,
      ellipsis: true,
      disabled: !isDraft
    },
    {
      name: DeliveryProcessDefinitionActionButtonType.Export,
      label: t('general.export'),
      ellipsis: true,
      click: showExportModal
    },
    {
      name: DeliveryProcessDefinitionActionButtonType.Dependencies,
      label: t('deliveryprocessdefinition.actions.dependencies.label'),
      click: navigateToDependenciesRoute
    },
    {
      name: DeliveryProcessDefinitionActionButtonType.GenerateReport,
      label: t('deliveryprocessdefinition.actions.generateReport.label'),
      click: generateReport
    },
    {
      name: DeliveryProcessDefinitionActionButtonType.GeckoConfiguration,
      label: t('deliveryprocessdefinition.actions.geckoConfiguration.label'),
      click: navigateToGeckoConfiguration
    }
  ];
};

const navigateToDependenciesRoute = () => {
  const id = props.detail.id;
  router.push({ name: 'deliveryprocessdefinition.detail.dependencies', params: { id } });
};

const navigateToGeckoConfiguration = () => {
  const id = props.detail.id;
  router.push({ name: 'deliveryprocessdefinition.detail.qualitycheck', params: { id } });
};

const showChangeStatusModal = () => {
  const modalContent = {
    title: t('deliveryprocessdefinition.actions.changeStatus.description'),
    component: DefinitionStatusForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showDuplicationModal = () => {
  const modalContent = {
    title: t('deliveryprocessdefinition.actions.duplicate.description'),
    component: DefinitionDuplicationForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showDryRunModal = async () => {
  const modalContent = {
    title: t('deliveryprocessdefinition.actions.dryrun.description'),
    component: DefinitionDryRunForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showCascadeDeleteModal = () => {
  const modalContent = {
    closable: false,
    title: t('deliveryprocessdefinition.actions.cascadeDelete.description'),
    component: DefinitionCascadeDeleteForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showExportModal = () => {
  const modalContent = {
    title: t('deliveryprocessdefinition.actions.export.description'),
    component: DefinitionExportForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const generateReport = async () => {
  const id = props.detail.id;
  await store.generateReport(id, {});
};
</script>

<style lang="scss" scoped></style>
